import { useCallback, useMemo, useRef, useState } from 'react';
import '../referrals.scss';
import DataGrid, { Column, Button as Butt, Editing, RequiredRule, Form, Toolbar, Item as ItemT, SearchPanel, Paging, StateStoring, Scrolling, Lookup } from 'devextreme-react/data-grid';
import RemisionesAPI from '../../../api/api.remmisions';
import { Item } from 'devextreme-react/form';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import { Button, Tooltip } from 'devextreme-react';
import { newToast } from '../../../components';
import { useAppSelector } from '../../../contexts/hooks';

interface TemporalRemissionsProps {
  onClose: () => void;
}

export const TemporalRemissions = ({ onClose }: TemporalRemissionsProps) => {
  const dataRef = useRef<DataGrid>(null);
  const { getTemporalRemisions, CreateTemporalRemision, SetTemporalRemision, DeleteTemporalRemision } = RemisionesAPI();
  const [selectedRowKeys, setSelectedRowKeys] = useState<any[]>([]);

  // Obtener laboratorios desde Redux - SOLO para selectores del formulario de creación/edición
  const laboratorios = useAppSelector((state) => state.configuration.Data?.LabReferencia);

  // Obtener estudios desde Redux - SOLO para selectores del formulario de creación/edición
  const estudios = useAppSelector((state) => state.configuration.Data?.Estudios);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Rmt_Id',
      async load() {
        const data = await getTemporalRemisions();
        return Promise.resolve({
          data: data || [],
        }).catch(() => {
          throw new Error('Error de carga de datos');
        });
      },
      insert: async (values) => {
        return CreateTemporalRemision(values)
          .then((res) => {
            if (res === 105) {
              dataRef.current?.instance.refresh();
            }
            return res;
          })
          .catch(() => {
            throw new Error('Error insertando la remisión temporal');
          });
      },
      update: (key, values) => {
        return new Promise((resolve, reject) => {
          dataRef.current?.instance
            .byKey(key)
            .then((originalRecord) => {
              // Mezclar el registro original con los valores modificados
              const fullRecord = { ...originalRecord, ...values };

              SetTemporalRemision(fullRecord)
                .then((res) => {
                  if (res === 107) {
                    dataRef.current?.instance.refresh();
                  }
                  resolve(res);
                })
                .catch((error) => {
                  reject(new Error('Error actualizando la remisión temporal'));
                });
            })
            .catch(reject);
        });
      },
      remove: (key) => {
        return DeleteTemporalRemision(key)
          .then(() => {
            dataRef.current?.instance.refresh();
          })
          .catch(() => {
            throw new Error('Error eliminando la remisión temporal');
          });
      },
    });
  }, []);

  const customDataSrc = new DataSource({
    store: customData,
    reshapeOnPush: true,
  });

  const handleRefresh = useCallback(() => {
    dataRef.current?.instance.refresh();
    newToast('SUCCESS', 'Datos actualizados', 5);
  }, []);

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    // Agregar nueva fila al grid
    dataRef.current?.instance.addRow();

    // Después de un pequeño delay, establecer los valores duplicados
    setTimeout(() => {
      const visibleRows = dataRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        // Establecer valores duplicados (excepto el ID)
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_Es_Id', rowData.Rmt_Es_Id);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_Lr_Id', rowData.Rmt_Lr_Id);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_FechaInicio', rowData.Rmt_FechaInicio);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_FechaFin', rowData.Rmt_FechaFin);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_Observaciones', rowData.Rmt_Observaciones ? `${rowData.Rmt_Observaciones} - Copia` : null);
        dataRef.current?.instance.cellValue(newRowIndex, 'Rmt_Estado', true);
      }
    }, 100);

    newToast('INFO', 'Remisión temporal duplicada. Complete la edición para guardar.', 3);
  }, []);

  // Renderizador personalizado para laboratorio - Usa datos del backend
  const renderLaboratorio = useCallback((cellData: any) => {
    const laboratorio = cellData.data?.Laboratorio;
    return <span>{laboratorio?.Lr_Nombre || 'No asignado'}</span>;
  }, []);

  // Renderizador personalizado para estudio - Usa datos del backend
  const renderEstudio = useCallback((cellData: any) => {
    const estudio = cellData.data?.Estudio;
    return (
      <div>
        <strong>{estudio?.Es_Codigo || ''}</strong> - {estudio?.Es_Nombre || 'No asignado'}
      </div>
    );
  }, []);

  return (
    <div style={{ height: '600px', width: '100%' }}>
      <DataGrid dataSource={customDataSrc} ref={dataRef} allowColumnReordering={true} height={'100%'} width={'100%'} columnAutoWidth={false} allowColumnResizing={true} showBorders={true} rowAlternationEnabled={true} hoverStateEnabled={true} onSelectionChanged={(e: any) => setSelectedRowKeys(e.selectedRowKeys)}>
        <StateStoring enabled={true} type='localStorage' storageKey='temporal-remissions-grid-state' />
        <SearchPanel visible={true} width={300} placeholder='Buscar remisiones temporales...' />
        <Scrolling columnRenderingMode='standard' mode='standard' useNative={true} />

        <Editing mode='form' allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} useIcons={true}>
          <Form labelLocation='top' colCount={2}>
            <Item
              dataField='Rmt_Es_Id'
              colSpan={2}
              editorType='dxSelectBox'
              editorOptions={{
                dataSource: estudios,
                displayExpr: (item: any) => (item ? `${item.Es_Codigo} - ${item.Es_Nombre}` : ''),
                valueExpr: 'Es_Id',
                searchEnabled: true,
                placeholder: 'Seleccione un estudio',
                stylingMode: 'outlined',
              }}>
              <RequiredRule message='El estudio es requerido' />
            </Item>
            <Item
              dataField='Rmt_Lr_Id'
              colSpan={2}
              editorType='dxSelectBox'
              editorOptions={{
                dataSource: laboratorios,
                displayExpr: 'Lr_Nombre',
                valueExpr: 'Lr_Id',
                searchEnabled: true,
                placeholder: 'Seleccione un laboratorio',
                stylingMode: 'outlined',
              }}>
              <RequiredRule message='El laboratorio es requerido' />
            </Item>
            <Item
              dataField='Rmt_FechaInicio'
              colSpan={1}
              editorType='dxDateBox'
              editorOptions={{
                stylingMode: 'outlined',
                displayFormat: 'dd/MM/yyyy',
              }}>
              <RequiredRule message='La fecha de inicio es requerida' />
            </Item>
            <Item
              dataField='Rmt_FechaFin'
              colSpan={1}
              editorType='dxDateBox'
              editorOptions={{
                stylingMode: 'outlined',
                displayFormat: 'dd/MM/yyyy',
              }}>
              <RequiredRule message='La fecha de fin es requerida' />
            </Item>
            <Item
              dataField='Rmt_Observaciones'
              editorType='dxTextArea'
              colSpan={2}
              editorOptions={{
                height: 100,
                placeholder: 'Ingrese observaciones...',
                stylingMode: 'outlined',
              }}
            />
            <Item
              dataField='Rmt_Estado'
              editorType='dxCheckBox'
              colSpan={2}
              editorOptions={{
                text: 'Activo',
              }}
            />
          </Form>
        </Editing>

        <Toolbar>
          <ItemT name='searchPanel' />
          <ItemT name='addRowButton' />
          <ItemT location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefreshTemporalRemissions' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefresh} />
            <Tooltip target={'#RefreshTemporalRemissions'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </ItemT>
        </Toolbar>

        <Column allowGrouping={false} caption='ID' visible={true} allowEditing={false} dataField='Rmt_Id' alignment='center' width={80} sortOrder='desc' />

        <Column allowGrouping={false} caption='ESTUDIO' visible={true} allowEditing={true} dataField='Rmt_Es_Id' alignment='left' minWidth={250}>
          <RequiredRule />
          <Lookup dataSource={estudios} displayExpr={(item: any) => (item ? `${item.Es_Codigo} - ${item.Es_Nombre}` : '')} valueExpr='Es_Id' />
        </Column>

        <Column allowGrouping={false} caption='LABORATORIO' visible={true} allowEditing={true} dataField='Rmt_Lr_Id' alignment='center' width={200}>
          <RequiredRule />
          <Lookup dataSource={laboratorios} displayExpr='Lr_Nombre' valueExpr='Lr_Id' />
        </Column>

        <Column allowGrouping={false} caption='FECHA INICIO' visible={true} allowEditing={true} dataField='Rmt_FechaInicio' alignment='center' width={120} dataType='date' format='dd/MM/yyyy'>
          <RequiredRule />
        </Column>

        <Column allowGrouping={false} caption='FECHA FIN' visible={true} allowEditing={true} dataField='Rmt_FechaFin' alignment='center' width={120} dataType='date' format='dd/MM/yyyy'>
          <RequiredRule />
        </Column>

        <Column allowGrouping={false} caption='OBSERVACIONES' visible={true} allowEditing={true} dataField='Rmt_Observaciones' alignment='left' minWidth={200} />

        <Column dataType='boolean' allowGrouping={false} caption='ESTADO' visible={true} allowEditing={true} dataField='Rmt_Estado' alignment='center' width={80}>
          <RequiredRule />
        </Column>

        <Column type='buttons' caption={'ACCIÓN'} width={110}>
          <Butt name='edit' />
          <Butt name='delete' />
          <Butt icon='copy' hint='Duplicar remisión temporal' onClick={handleDuplicate} />
        </Column>
      </DataGrid>
    </div>
  );
};
